// AuditSummary.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReusableTable from "../../ReusableTable/ReusableTable";
import { env } from "../../../constants/env";
import { useAuth } from "../../../context/AuthContext";
import classes from "./AuditSummary.module.css";
import { db } from "../../../utils/offlineDB";

// Generate consistent ID for offline/online records
const makeSummaryId = (s, labId) =>
  s._id || s.id || `${labId}-${s.timestamp || s.dateCreated}-${s.totalScore}`;

const AuditSummary = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const userRole = user?.role;

  const [selectedSummary, setSelectedSummary] = useState(null);

  const auditSummaryColumns = [
    { label: "Date Created", field: "dateCreated" },
    { label: "Total Score", field: "totalScore" },
    { label: "Stars Rating", field: "starsText" },
  ];

  const normalizeSummary = (s) => {
    const earnedStars = s.starsValue ?? s.stars ?? 0;
    const totalStars = 5;

    return {
      id: s._id || s.id,
      labName: s.labName ?? "—",
      hpczLicense: s.hpczLicense ?? "—",
      dateCreated: new Date(
        s.dateCreated || s.createdAt || s.timestamp
      ).toLocaleDateString("en-GB"), // DD/MM/YYYY
      totalScore: s.totalScore ?? 0,
      starsValue: earnedStars,
      starsText: "★".repeat(earnedStars) + "☆".repeat(totalStars - earnedStars),
      sections: s.sections || [],
    };
  };

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        setLoading(true);
        let summariesData = [];

        // 📴 OFFLINE
        if (!navigator.onLine) {
          const local = await db.offlineAuditSummaries
            .where("labId")
            .equals(labId)
            .toArray();
          summariesData = local.map(normalizeSummary);
        }
        // 🌐 ONLINE
        else {
          const res = await fetch(
            `${env.kataApiUrl}/api/certification/audit-summary?labId=${labId}`
          );

          if (!res.ok) throw new Error("Failed to fetch audit summaries");

          const remote = await res.json();
          summariesData = remote.map(normalizeSummary);

          // Cache online summaries locally, but do NOT overwrite offline edits
          for (const summary of remote) {
            const id = makeSummaryId(summary, labId);
            const existing = await db.offlineAuditSummaries.get(id);

            // Only put if:
            // 1️⃣ Record does not exist
            // 2️⃣ Record exists but is not offline (offline edits are preserved)
            if (!existing || existing.offline === false) {
              await db.offlineAuditSummaries.put({
                ...summary,
                id,
                labId,
                labName: summary.labName,
                hpczLicense: summary.hpczLicense,
                dateCreated: summary.dateCreated,
                totalScore: summary.totalScore,
                stars: summary.starsValue,
                sections: summary.sections,
                offline: existing?.offline === true ? true : false, // preserve offline edits
                lastSynced: new Date().toISOString(),
                timestamp: Date.now(),
              });
            }
          }
        }

        setSummaries(summariesData);
      } catch (err) {
        console.error("Failed to fetch audit summaries", err);
      } finally {
        setLoading(false);
      }
    };

    if (labId) fetchSummaries();
  }, [labId]);

  return (
    <div>
      <ReusableTable
        title="Audit Summary"
        showTopAdd={userRole === "ASSESSOR" ? summaries.length > 0 : false}
        columns={auditSummaryColumns}
        data={summaries}
        loading={loading}
        onAdd={
          userRole === "ASSESSOR" ? () => navigate("add-audit-summary") : false
        }
        useOverlay
        onShowDetails={(row) => setSelectedSummary(row)}
      />

      {/* Overlay Drawer */}
      {selectedSummary && (
        <div className={classes.overlay}>
          <div className={classes.drawer}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h3>{selectedSummary.labName}</h3>
                <div className={classes.licenseContainer}>
                  {selectedSummary.hpczLicense}
                </div>
              </div>
              <button
                className={classes.closeBtn}
                onClick={() => setSelectedSummary(null)}
              >
                ✕
              </button>
            </div>

            <div
              className={`${classes.mainStats} d-flex align-items-center justify-content-between my-4`}
            >
              <div>
                <h5>Created</h5>
                <p>{selectedSummary.dateCreated}</p>
              </div>
              <div>
                <h5>Total score</h5>
                <p>{selectedSummary.totalScore}</p>
              </div>
              <div>
                <h5>Stars rating</h5>
                <span>
                  {selectedSummary.starsValue} {selectedSummary.starsText}
                </span>
              </div>
            </div>

            <h5>Individual ratings</h5>
            <ul>
              {selectedSummary.sections?.map((s, i) => (
                <li key={i}>
                  <div
                    className={`${classes.individualStats} d-flex align-items-center justify-content-between`}
                  >
                    <div>
                      <p>{s.section}</p>
                    </div>
                    <div>
                      <p>{s.score}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditSummary;